import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  Gift,
  Home,
  LogIn,
  LogOut,
  Share2,
  User,
  UserPlus,
  Wallet,
  X,
} from "lucide-react";
import API from "../services/api";
import WhatsAppGlyph from "./WhatsAppGlyph";
import { buildWhatsAppUrl, openExternalLink, shareAppLink } from "../utils/appInteractions";

function Sidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [settings, setSettings] = useState({ whatsapp_number: "" });

  useEffect(() => {
    API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {});
  }, []);

  const close = () => setIsOpen(false);
  const go = (path, protect = false) => { close(); navigate(protect && !user ? "/login" : path); };
  const logout = () => { localStorage.removeItem("user"); localStorage.removeItem("posm_session_active"); close(); navigate("/login"); };

  const openWhatsapp = () => {
    const phone = getPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello, I need support");
    if (url) openExternalLink(url);
  };

  const shareApp = async () => {
    const code = String(user?.referral_code || "").trim();
    const url = `${window.location.origin}/login?tab=register${code ? `&ref=${encodeURIComponent(code)}` : ""}`;
    try { const shared = await shareAppLink({ title: "Play Online Satta Matka", text: code ? `Join with my code ${code}` : "Join Play Online Satta Matka", url }); if (!shared) alert("Link copied!"); } catch (_) {}
  };

  return (
    <>
      {isOpen && <div onClick={close} style={overlay} />}
      <aside style={{ ...drawer, transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}>

        {/* Header */}
        <header style={drawerHeader}>
          <div style={brand}>
            <span style={brandDot} />
            <span style={brandName}>PLAY ONLINE MATKA</span>
          </div>
          <button type="button" style={closeBtn} onClick={close}><X size={16} color="rgba(255,255,255,0.7)" /></button>
        </header>

        {/* User card */}
        {user ? (
          <div style={userCard}>
            <div style={avatar}>{String(user.name || "U").charAt(0).toUpperCase()}</div>
            <div style={userInfo}>
              <div style={userName}>{user.name || "Player"}</div>
              <div style={userPhone}>{user.phone ? `+91 ${String(user.phone).slice(-10)}` : ""}</div>
            </div>
          </div>
        ) : (
          <div style={guestCard}>
            <div style={guestText}>Welcome! Login to play</div>
            <div style={authRow}>
              <button type="button" style={loginBtn} onClick={() => { close(); navigate("/login"); }}><LogIn size={13} /> Login</button>
              <button type="button" style={regBtn} onClick={() => { close(); navigate("/login?tab=register"); }}><UserPlus size={13} /> Register</button>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav style={nav}>
          <Item icon={Home}     label="Home"        color="#f97316" onClick={() => go("/dashboard")} />
          <Item icon={Wallet}   label="Deposit"     color="#22c55e" onClick={() => go("/deposit", true)} />
          <Item icon={BarChart3}label="Charts"      color="#e879f9" onClick={() => go("/charts")} />
          <Sep />
          <Item icon={Gift}     label="Refer & Earn"color="#fbbf24" onClick={() => go("/refer", true)} />
          <Item icon={User}     label="My Profile"  color="#a78bfa" onClick={() => go("/profile", true)} />
          {user && <>
            <Sep />
            <Item icon={Share2} label="Share App"   color="#38bdf8" onClick={shareApp} />
            <Item icon={LogOut} label="Logout"      color="#f87171" onClick={logout} />
          </>}
        </nav>

        {/* WhatsApp support footer */}
        <footer style={foot}>
          <button type="button" style={waBtn} onClick={openWhatsapp}>
            <span style={waIcon}><WhatsAppGlyph size={17} color="#fff" /></span>
            <span style={waText}><strong>WhatsApp Support</strong><small>Tap to chat now</small></span>
            <ChevronRight size={14} color="rgba(255,255,255,0.4)" />
          </button>
        </footer>
      </aside>
    </>
  );
}

function Item({ icon: Icon, label, color, onClick }) {
  return (
    <button type="button" style={navItem} onClick={onClick}>
      <span style={{ ...iconWrap, background: `${color}20`, color }}><Icon size={17} /></span>
      <span style={navLabel}>{label}</span>
      <ChevronRight size={13} color="#e2e8f0" />
    </button>
  );
}
function Sep() { return <div style={sep} />; }

function getPhone(val) {
  const d = String(val || "").replace(/\D/g, "");
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith("91")) return d;
  return d;
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 999, backdropFilter: "blur(3px)" };
const drawer = { position: "fixed", inset: "0 auto 0 0", width: 272, maxWidth: "85vw", height: "100dvh", background: "linear-gradient(180deg,#0f0e1a 0%,#1a1830 100%)", zIndex: 1000, transition: "transform 0.3s cubic-bezier(0.16,1,0.3,1)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "8px 0 40px rgba(0,0,0,0.4)" };

const drawerHeader = { height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 };
const brand = { display: "flex", alignItems: "center", gap: 8 };
const brandDot = { width: 8, height: 8, borderRadius: "50%", background: "#f97316", boxShadow: "0 0 8px #f97316" };
const brandName = { color: "#fff", fontWeight: 900, fontSize: 13, letterSpacing: "0.03em" };
const closeBtn = { width: 30, height: 30, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, background: "transparent", display: "grid", placeItems: "center", cursor: "pointer" };

const userCard = { margin: "12px 12px 4px", padding: "12px 14px", borderRadius: 14, background: "linear-gradient(135deg,rgba(249,115,22,0.15),rgba(249,115,22,0.05))", border: "1px solid rgba(249,115,22,0.2)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 };
const avatar = { width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", fontWeight: 900, fontSize: 17, display: "grid", placeItems: "center", flexShrink: 0 };
const userInfo = { minWidth: 0 };
const userName = { color: "#fff", fontWeight: 800, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const userPhone = { color: "rgba(255,255,255,0.5)", fontSize: 11, marginTop: 2 };

const guestCard = { margin: "12px 12px 4px", padding: "14px", borderRadius: 14, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 };
const guestText = { color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, marginBottom: 10 };
const authRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const loginBtn = { height: 36, border: 0, borderRadius: 10, background: "#f97316", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontWeight: 800, fontSize: 12, cursor: "pointer" };
const regBtn = { ...loginBtn, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" };

const nav = { flex: 1, overflowY: "auto", minHeight: 0, padding: "8px 10px", display: "grid", alignContent: "start", gap: 2 };
const navItem = { height: 46, display: "grid", gridTemplateColumns: "38px 1fr 16px", alignItems: "center", border: 0, background: "transparent", textAlign: "left", borderRadius: 10, padding: "0 8px", cursor: "pointer", color: "rgba(255,255,255,0.85)" };
const iconWrap = { width: 30, height: 30, display: "grid", placeItems: "center", borderRadius: 8 };
const navLabel = { fontSize: 14, fontWeight: 700, color: "#fff" };
const sep = { height: 1, background: "rgba(255,255,255,0.07)", margin: "4px 0" };

const foot = { padding: "10px 10px calc(10px + env(safe-area-inset-bottom, 0px))", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0, background: "linear-gradient(180deg,#0f0e1a 0%,#1a1830 100%)" };
const waBtn = { width: "100%", border: 0, borderRadius: 12, background: "linear-gradient(135deg,#25D366,#17b857)", color: "#fff", display: "grid", gridTemplateColumns: "34px 1fr 16px", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer" };
const waIcon = { width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "grid", placeItems: "center" };
const waText = { display: "grid", gap: 2, textAlign: "left", fontSize: 12 };

export default Sidebar;
